/* eslint-disable indent */
import React from "react";
import cloneDeep from "lodash/cloneDeep";
import {
  getNewDate,
  convertDateTimezone,
  getCandidateVideoInterviewData,
  testScoreRangeHasTextScores,
} from "utils/helperFunctions";
import { RolesAccessLibrary } from "utils/rolesAccessFunctions";
import { fromJS, List, Map } from "immutable";
import { calculateIfAllScoreRangesMet } from "components/CandidateProfileComponent/helpers";

const sortByStatus = (candidates) =>
  candidates.sort(
    (cand1, cand2) =>
      cand2.get("candidateStatus") - cand1.get("candidateStatus")
  );

export const dateRecent = (candidates) =>
  candidates.sort((candidate1, candidate2) =>
    findEventDate(candidate1) < findEventDate(candidate2) ? 1 : -1
  );

export const firstName = (candidates) => {
  const sortedArr = candidates.sort((candidate1, candidate2) => {
    if (
      candidate1.get("testTakerFirstName").toLowerCase() <
      candidate2.get("testTakerFirstName").toLowerCase()
    ) {
      return -1;
    }
    if (
      candidate1.get("testTakerFirstName").toLowerCase() >
      candidate2.get("testTakerFirstName").toLowerCase()
    ) {
      return 1;
    }
    return candidate1.get("testTakerLastName").toLowerCase() <
      candidate2.get("testTakerLastName").toLowerCase()
      ? -1
      : 1;
  });
  return sortedArr;
};

export const lastName = (candidates) => {
  const sortedArr = candidates.sort((candidate1, candidate2) => {
    if (
      candidate1.get("testTakerLastName").toLowerCase() <
      candidate2.get("testTakerLastName").toLowerCase()
    ) {
      return -1;
    }
    if (
      candidate1.get("testTakerLastName").toLowerCase() >
      candidate2.get("testTakerLastName").toLowerCase()
    ) {
      return 1;
    }
    return candidate1.get("testTakerFirstName").toLowerCase() <
      candidate2.get("testTakerFirstName").toLowerCase()
      ? -1
      : 1;
  });
  return sortedArr;
};

export const rating = (candidates) =>
  candidates.sort((candidate1, candidate2) => {
    if (
      candidate2.get("averageRating") === null &&
      candidate1.get("averageRating") === null
    ) {
      return 0;
    }
    if (
      candidate2.get("averageRating") === null &&
      candidate1.get("averageRating") !== null
    ) {
      return -1;
    }
    if (
      candidate2.get("averageRating") !== null &&
      candidate1.get("averageRating") === null
    ) {
      return 1;
    }
    return (
      Number(candidate2.get("averageRating")) -
      Number(candidate1.get("averageRating"))
    );
  });

export const rankingScore = (candidates) =>
  candidates.sort((candidate1, candidate2) => {
    if (
      candidate2.get("rankingScore") === null &&
      candidate1.get("rankingScore") === null
    ) {
      return 0;
    }
    if (
      candidate2.get("rankingScore") === null &&
      candidate1.get("rankingScore") !== null
    ) {
      return -1;
    }
    if (
      candidate2.get("rankingScore") !== null &&
      candidate1.get("rankingScore") === null
    ) {
      return 1;
    }
    return (
      Number(candidate2.get("rankingScore")) -
      Number(candidate1.get("rankingScore"))
    );
  });

export const videoInterviewOverallStatus = (candidates) =>
  candidates.sort((candidate1, candidate2) => {
    const { candidateVideoInterviewDataToShow: candidate1ViData } =
      getCandidateVideoInterviewData(candidate1);
    const { candidateVideoInterviewDataToShow: candidate2ViData } =
      getCandidateVideoInterviewData(candidate2);

    const candidate1OverallStatus =
      candidate1ViData &&
      !candidate1ViData.get("outDated") &&
      candidate1ViData.get("evaluationsPending");
    const candidate2OverallStatus =
      candidate2ViData &&
      !candidate2ViData.get("outDated") &&
      candidate2ViData.get("evaluationsPending");

    if (candidate2OverallStatus === null && candidate1OverallStatus === null) {
      return 0;
    }
    if (candidate2OverallStatus === null && candidate1OverallStatus !== null) {
      return -1;
    }
    if (candidate2OverallStatus !== null && candidate1OverallStatus === null) {
      return 1;
    }
    return Number(candidate1OverallStatus) - Number(candidate2OverallStatus);
  });

export const userVideoInterviewEvaluationStatus = (candidates) =>
  candidates.sort((candidate1, candidate2) => {
    const { candidateVideoInterviewDataToShow: candidate1ViData } =
      getCandidateVideoInterviewData(candidate1);
    const { candidateVideoInterviewDataToShow: candidate2ViData } =
      getCandidateVideoInterviewData(candidate2);

    const candidate1EvaluationStatus =
      candidate1ViData &&
      !candidate1ViData.get("outDated") &&
      candidate1ViData.get("currentUserEvaluated");
    const candidate2EvaluationStatus =
      candidate2ViData &&
      !candidate2ViData.get("outDated") &&
      candidate2ViData.get("currentUserEvaluated");
    if (
      candidate2EvaluationStatus === null &&
      candidate1EvaluationStatus === null
    ) {
      return 0;
    }
    if (
      candidate2EvaluationStatus === null &&
      candidate1EvaluationStatus !== null
    ) {
      return -1;
    }
    if (
      candidate2EvaluationStatus !== null &&
      candidate1EvaluationStatus === null
    ) {
      return 1;
    }
    return (
      Number(candidate1EvaluationStatus) - Number(candidate2EvaluationStatus)
    );
  });

export const videoInterviewScore = (candidates) =>
  candidates.sort((candidate1, candidate2) => {
    const { candidateVideoInterviewDataToShow: candidate1ViData } =
      getCandidateVideoInterviewData(candidate1);
    const { candidateVideoInterviewDataToShow: candidate2ViData } =
      getCandidateVideoInterviewData(candidate2);

    const candidate1Score =
      candidate1ViData &&
      !candidate1ViData.get("outDated") &&
      candidate1ViData.get("totalScore");
    const candidate2Score =
      candidate2ViData &&
      !candidate2ViData.get("outDated") &&
      candidate2ViData.get("totalScore");

    if (candidate2Score === null && candidate1Score === null) {
      return 0;
    }
    if (candidate2Score === null && candidate1Score !== null) {
      return -1;
    }
    if (candidate2Score !== null && candidate1Score === null) {
      return 1;
    }
    return Number(candidate2Score) - Number(candidate1Score);
  });

export const determineCandidatesThatPass = (
  candidates,
  filterOptions,
  currentStageId,
  stageIdToStageNameMap,
  scoreRanges,
  isAU,
  fromResultsV2
) => {
  let passingCount = 0;
  Object.values(candidates).forEach((candidate) => {
    if (
      determineIfCandidatePassesFilterOptions(
        candidate,
        filterOptions,
        currentStageId,
        stageIdToStageNameMap,
        scoreRanges,
        isAU,
        fromResultsV2
      )
    ) {
      passingCount += 1;
    }
  });
  return passingCount;
};

export const sortCandidates = (
  stageCandidatesObj,
  criteria,
  columnSort = null,
  jobData = null
) => {
  const stageCandidatesArray = [];
  stageCandidatesObj.keySeq().forEach((candidateId) => {
    const candidate = stageCandidatesObj
      .get(candidateId)
      .set("candidateId", candidateId);
    stageCandidatesArray.push(candidate);
  });

  if (columnSort && columnSort.order !== null) {
    sortByColumn(stageCandidatesArray, columnSort, jobData);
    return stageCandidatesArray.map((candidateObj) =>
      candidateObj.get("candidateId")
    );
  }

  // This function could use some refactoring because it is sorting the candidate more than it needs to.
  switch (criteria) {
    case "Date Applied - Recent":
      sortByStatus(dateRecent(stageCandidatesArray));
      break;
    case "First Name - A to Z":
      sortByStatus(firstName(dateRecent(stageCandidatesArray)));
      break;
    case "Last Name - A to Z":
      sortByStatus(lastName(dateRecent(stageCandidatesArray)));
      break;
    case "Rating - High to Low":
      sortByStatus(rating(dateRecent(stageCandidatesArray)));
      break;
    case "Talent Signal - High to Low":
      sortByStatus(rankingScore(dateRecent(stageCandidatesArray)));
      break;
    case "Video - Overall - Complete to Pending":
      sortByStatus(
        videoInterviewOverallStatus(dateRecent(stageCandidatesArray))
      );
      break;
    case "Video - My status - Pending to Evaluated":
      sortByStatus(
        userVideoInterviewEvaluationStatus(dateRecent(stageCandidatesArray))
      );
      break;
    case "Video Interview Score - High to Low":
      sortByStatus(videoInterviewScore(dateRecent(stageCandidatesArray)));
      break;
    default:
      stageCandidatesArray.sort(
        (candidate1, candidate2) =>
          getNewDate(candidate2.get("latestActivityDate")) -
          getNewDate(candidate1.get("latestActivityDate"))
      );
      break;
  }
  // return an array of sorted candidates' ids
  return stageCandidatesArray.map((candidateObj) =>
    candidateObj.get("candidateId")
  );
};

const sortByColumn = (candidates, columnSort, jobData) =>
  candidates.sort((candidateA, candidateB) => {
    let returnVal = 0;

    if (columnSort.subcategory === "latestActivityDate") {
      // if we are sorting by date no need to sort it again if is the same, that is why we are returning here instead of setting the value to returnVal
      return compareGeneral(candidateA, candidateB, columnSort, jobData);
    }

    if (columnSort.subcategory === "fullName") {
      returnVal =
        compareByFullName(candidateA, candidateB) *
        (columnSort.order === "up" ? 1 : -1);
    } else if (columnSort.category.toLowerCase() === "general") {
      returnVal = compareGeneral(candidateA, candidateB, columnSort, jobData);
    } else {
      returnVal = compareCandidateColumns(candidateA, candidateB, columnSort);
    }

    // If they are the same then compare by latest activity date.
    return returnVal === 0
      ? compareGeneral(candidateA, candidateB, {
          subcategory: "latestActivityDate",
          order: columnSort.order,
        })
      : returnVal;
  });

// This function sorts the candidates by column category, if the values are the same then it looks at the testTaker name, if those are the same then we check latestActivityDate
const compareCandidateColumns = (
  candidateA,
  candidateB,
  { category, subcategory, order }
) => {
  const candidateATest = getTestEvent(candidateA, category);
  const candidateBTest = getTestEvent(candidateB, category);

  // CandidateA contains incoming category but CandidateB does not
  if (candidateATest && !candidateBTest) {
    return order === "up" ? -1 : 1;
    // CandidateB contains incoming category but CandidateA does not
  }
  if (!candidateATest && candidateBTest) {
    return order === "up" ? 1 : -1;
  }

  // If the column we are sorting by is videoInterview we need to check if the user has a video interview, otherwise might order N/A values above 0.
  if (category === "videoInterview") {
    const eitherAreNotCompleted = checkVICompleted(
      candidateATest,
      candidateBTest,
      order
    );
    if (eitherAreNotCompleted !== 0) {
      return eitherAreNotCompleted;
    }
  }

  const candidateAValue = getCandidateValue(
    candidateATest,
    subcategory,
    category
  );
  const candidateBValue = getCandidateValue(
    candidateBTest,
    subcategory,
    category
  );

  if (candidateAValue > candidateBValue) {
    return order === "up" ? -1 : 1;
  }
  if (candidateAValue < candidateBValue) {
    return order === "up" ? 1 : -1;
  }

  return 0; // If the values are the same return 0
};

const checkVICompleted = (a, b, order) => {
  if (a && a.get("dateCompleted") && !(b && b.get("dateCompleted"))) {
    return order === "up" ? -1 : 1;
  }
  if (b && b.get("dateCompleted") && !(a && a.get("dateCompleted"))) {
    return order === "up" ? 1 : -1;
  }
  return 0;
};

const compareByFullName = (candidateA, candidateB) => {
  const candidateAFullName = `${candidateA.get(
    "testTakerFirstName"
  )} ${candidateA.get("testTakerLastName")}`;
  const candidateBFullName = `${candidateB.get(
    "testTakerFirstName"
  )} ${candidateB.get("testTakerLastName")}`;

  return candidateAFullName.localeCompare(candidateBFullName);
};

const compareGeneral = (
  candidateA,
  candidateB,
  { subcategory, order },
  jobData
) => {
  const valA = getGeneralValue(candidateA, subcategory, order, jobData);
  const valB = getGeneralValue(candidateB, subcategory, order, jobData);

  if (valA > valB) {
    return order === "up" ? -1 : 1;
  }
  if (valA < valB) {
    return order === "up" ? 1 : -1;
  }
  return 0; // If the values are the same return 0
};

const getGeneralValue = (candidate, subcategory, order, jobData) => {
  switch (subcategory) {
    case "scoreRangesMet": {
      return calculateIfAllScoreRangesMet(candidate, jobData, true) ? 1 : 0;
    }
    case "latestActivityDate": {
      return candidate.get(subcategory);
    }
    case "expirationDate": {
      // Kinda hacky way to guarantee that candidates with actual expiration dates have priority over those that don't even if the order is reversed.
      const noExpirationSortValue = order === "up" ? "" : "a";

      if (!(candidate && candidate.get("testEvents"))) {
        return noExpirationSortValue;
      }

      let eventExpiration = null;
      const testEventIdSet = new Set();

      candidate.get("testEvents").forEach((testEventsObj, key) => {
        if (!testEventsObj) {
          return;
        }

        if (key === "videoInterview" || key.includes("additional")) {
          if (
            !testEventsObj.get("eventExpirationDate") ||
            !testEventsObj.get("incompleteFlag") ||
            testEventIdSet.has(testEventsObj.get("testEventId"))
          ) {
            return;
          }

          if (
            !eventExpiration ||
            isSecondDateCloserToToday(
              eventExpiration,
              testEventsObj.get("eventExpirationDate")
            )
          ) {
            testEventIdSet.add(testEventsObj.get("testEventId"));
            eventExpiration = testEventsObj.get("eventExpirationDate");
          }
          return;
        }

        testEventsObj.forEach((testEventObj) => {
          if (
            !testEventObj ||
            !testEventObj.get("eventExpirationDate") ||
            !testEventObj.get("incompleteFlag") ||
            testEventIdSet.has(testEventObj.get("testEventId"))
          ) {
            return;
          }

          if (
            testEventObj.get("eventExpirationDate") &&
            (!eventExpiration ||
              isSecondDateCloserToToday(
                eventExpiration,
                testEventsObj.get("eventExpirationDate")
              ))
          ) {
            testEventIdSet.add(testEventObj.get("testEventId"));
            eventExpiration = testEventObj.get("eventExpirationDate");
          }
        });
      });

      if (eventExpiration === null) {
        return noExpirationSortValue;
      }

      return eventExpiration;
    }
    default: {
      return Number(candidate.get(subcategory));
    }
  }
};

const isSecondDateCloserToToday = (date1, date2) => {
  const val1 = Math.abs(Date.now() - new Date(date1));
  const val2 = Math.abs(Date.now() - new Date(date2));
  // if the dates are the same then it should return false
  return val1 > val2;
};

const getCandidateValue = (candidateTest, subcategory, category) => {
  if (!candidateTest) {
    return null;
  }

  const candidateValue = candidateTest.get(subcategory);
  if (!isNaN(Number(candidateValue))) {
    return Number(candidateValue);
  }

  if (rankingOrRecommendationObj[Number(category)]) {
    return rankingOrRecommendationObj[Number(category)][candidateValue];
  }

  return null;
};

export const getTestEvent = (candidate, category, interviewTemplateId) => {
  switch (category) {
    case "general":
      return null;
    case "videoInterview":
      return candidate && candidate.getIn(["testEvents", category]);
    case "liveInterviews": {
      const liveInterviewResults =
        candidate && candidate.getIn(["testEvents", category]);

      if (!(liveInterviewResults && liveInterviewResults.size)) {
        return null;
      }

      // first find the live interview result that matches the interviewTemplateId
      // then sort the test events from newest to oldest, and get the first one (newest)
      // since in the results grid view we show the most recent live interview result for each interviewTemplateId
      const filteredResult = liveInterviewResults
        .filter(
          (liveInterview) =>
            String(liveInterview.get("interviewTemplateId")) ===
            interviewTemplateId
        )
        .sort((a, b) => b.get("testEventId") - a.get("testEventId"))
        .first();

      return filteredResult;
    }
    case "proctorings": {
      const proctoringEvents =
        candidate && candidate.getIn(["testEvents", category]);

      if (!(proctoringEvents && proctoringEvents.size)) {
        return null;
      }
      // use the proctoring result from the most recent scheduled testEvent
      const latestScheduledTestEventId = proctoringEvents
        .keySeq()
        .sort((a, b) => b - a)
        .first();
      return proctoringEvents.get(latestScheduledTestEventId);
    }

    default: {
      let categoryToUse = category;

      if (category.includes("express")) {
        const testId = category.split("_")[0];
        categoryToUse = testId;
      }

      // Since it is formatted as a map we don't have access to the key, so we need to call .first() to get the value
      const testsObj =
        candidate &&
        candidate.getIn(["testEvents", categoryToUse]) &&
        candidate.getIn(["testEvents", categoryToUse]).first();
      if (!(testsObj && testsObj.get("tests"))) {
        return null;
      }
      return testsObj.get("tests").first();
    }
  }
};

// Used to standardize the test events structure for shared logic between Results v1 and v2
export const getTestEvents = (candidate, fromResultsV2) => {
  let testEvents = new Map();

  if (!(candidate && candidate.get("testEvents"))) {
    return testEvents;
  }

  if (!fromResultsV2) {
    return candidate.get("testEvents");
  }

  // Reformat the Results v2 testEvents to match the v1 structure here, i.e.
  // testEvents: {[testEventId]: {testEventData}, ...}
  candidate.get("testEvents").forEach((testEventObject, testIdString) => {
    if (testIdString === "liveInterviews") {
      testEvents = testEvents.set(testIdString, testEventObject);
      return;
    }

    // Maintain any video interview events as is
    if (testIdString === "videoInterview") {
      testEvents = testEvents.set(testIdString, testEventObject);
      return;
    }

    // Resume/application only events
    if (testIdString.includes("additional")) {
      testEvents = testEvents.set(testIdString.substring(11), testEventObject);
      return;
    }

    testEventObject.forEach((event, testEventId) => {
      if (!event) {
        return;
      }

      if (!testEvents.has(testEventId)) {
        testEvents = testEvents.set(testEventId, event);
      } else {
        // If the testEvent has already been added, just add any new test data to its tests List
        const testsToAdd = event.get("tests");
        if (testsToAdd) {
          testEvents = testEvents.setIn(
            [testEventId, "tests"],
            testEvents.getIn([testEventId, "tests"]).concat(testsToAdd)
          );
        }
      }
    });
  });

  // Sort the test events from newest to oldest
  return testEvents
    .filter((testEvent) => testEvent.has("tests"))
    .sort((a, b) => b.get("testEventId") - a.get("testEventId"))
    .concat(testEvents.filter((testEvent) => !testEvent.has("tests")));
};

export const findEventDate = (
  candidate,
  otherParameter = false,
  isAU = false,
  fromResultsV2 = false
) => {
  const testEvents = getTestEvents(candidate, fromResultsV2);

  switch (otherParameter || String(candidate.get("candidateStatus"))) {
    case "2":
      return testEvents.reduce((date, testEvent, testId) => {
        const dateEventTaken = convertDateTimezone(
          testEvent.get("dateEventTaken") ??
            getLiveInterviewFallbackDateValue(
              testId,
              testEvent,
              "dateCompleted"
            ),
          isAU
        );

        if (dateEventTaken && getNewDate(dateEventTaken) > date) {
          return getNewDate(dateEventTaken);
        }

        return date;
      }, getNewDate("1970-01-01 01:01:01"));
    case "1":
      return testEvents.reduce((date, testEvent, testId) => {
        const trueDateEventStarted = convertDateTimezone(
          testEvent.get("trueDateEventStarted") ??
            getLiveInterviewFallbackDateValue(testId, testEvent, "dateCreated"),
          isAU
        );

        if (trueDateEventStarted && getNewDate(trueDateEventStarted) > date) {
          return getNewDate(trueDateEventStarted);
        }

        return date;
      }, getNewDate("1970-01-01 01:01:01"));
    case "0":
      return testEvents.reduce((date, testEvent, testId) => {
        const dateEventAdded = convertDateTimezone(
          testEvent.get("dateEventAdded") ??
            getLiveInterviewFallbackDateValue(testId, testEvent, "dateCreated"),
          isAU
        );

        if (dateEventAdded && getNewDate(dateEventAdded) > date) {
          return getNewDate(dateEventAdded);
        }

        return date;
      }, getNewDate("1970-01-01 01:01:01"));
    case "earliestEventCompleted":
      return testEvents.reduce((date, testEvent, testId) => {
        const dateEventTaken = convertDateTimezone(
          testEvent.get("dateEventTaken") ??
            getLiveInterviewFallbackDateValue(testId, testEvent, "dateCreated"),
          isAU
        );

        if (
          dateEventTaken &&
          (date === null || getNewDate(dateEventTaken) < date)
        ) {
          return getNewDate(dateEventTaken);
        }

        return date;
      }, null);
    default:
      return convertDateTimezone(candidate.get("appliedOn"), isAU);
  }
};

const getLiveInterviewFallbackDateValue = (
  eventKey,
  testEvents,
  dateKeyName
) => {
  if (eventKey !== "liveInterviews") {
    return undefined;
  }
  const testEvent = testEvents.first();
  return testEvent.get(dateKeyName) ?? undefined;
};

export const checkAdvancedFiltersLength = (advFilters) => {
  let startingAdvancedFilterCount = 0;
  if (advFilters.rankingScoreRange && advFilters.rankingScoreRange.value) {
    startingAdvancedFilterCount += 1;
  }
  if (
    advFilters.proctoringScoreRange &&
    advFilters.proctoringScoreRange.value
  ) {
    startingAdvancedFilterCount += 1;
  }

  const testOnlyAdvFilters = cloneDeep(advFilters);
  delete testOnlyAdvFilters.rankingScoreRange;
  delete testOnlyAdvFilters.proctoringScoreRange;

  return Object.keys(testOnlyAdvFilters).reduce(
    (count, testId) =>
      count +
      Object.values(testOnlyAdvFilters[testId]).filter(
        (rangeParameter) => rangeParameter.scoreRange
      ).length,
    startingAdvancedFilterCount
  );
};

export const initialFilterParametersDifferent = {
  dateApplied: (e) => (e.from !== "" && e.to !== "" ? 1 : 0),
  dateInvited: (e) => (e.from !== "" && e.to !== "" ? 1 : 0),
  dateStarted: (e) => (e.from !== "" && e.to !== "" ? 1 : 0),
  dateCompleted: (e) => (e.from !== "" && e.to !== "" ? 1 : 0),
  source: (e) => (e !== "" ? 1 : 0),
  rating: (e) => (e !== "" ? 1 : 0),
  label: (e) => (e !== "" ? 1 : 0),
  scheduledBy: (e) => (e.length > 0 ? 1 : 0),
  scoreRangeFilters: (e) => (e.length > 0 ? e.length : 0),
  advancedFilters: (e) =>
    checkAdvancedFiltersLength(e) > 0 ? checkAdvancedFiltersLength(e) : 0,
  archiveReason: (e) => (e !== "" ? 1 : 0),
};
export const determineIfCandidatePassesFilterOptions = (
  candidate,
  filterOptions,
  stageId,
  stageIdToStageNameMap,
  scoreRanges,
  isAU,
  fromResultsV2
) => {
  const applicableFilterOptions = determineApplicableFilters(
    candidate.get("candidateStatus"),
    stageId
  );
  let filterPassed = true;
  applicableFilterOptions.forEach((filterOption) => {
    const filterParameter = filterOptions[filterOption];
    if (
      initialFilterParametersDifferent[filterOption](filterParameter) &&
      filterPassed
    ) {
      filterPassed = determineFilterPassed(
        candidate,
        filterOption,
        filterParameter,
        stageIdToStageNameMap,
        scoreRanges,
        isAU,
        fromResultsV2
      );
    }
  });
  return filterPassed;
};
const determineApplicableFilters = (candidateStatus, stageId) => {
  // when viewing candidates in archive
  if (stageId === "archive") {
    return [
      "dateApplied",
      "source",
      "rating",
      "label",
      "scheduledBy",
      "archiveReason",
      "scoreRangeFilters",
      "advancedFilters",
    ];
  }
  // when viewing candidates in all
  if (stageId === "all") {
    return [
      "dateApplied",
      "source",
      "rating",
      "label",
      "scheduledBy",
      "scoreRangeFilters",
      "advancedFilters",
    ];
  }
  // when viewing candidates in a pipeline stage
  switch (candidateStatus) {
    // invited candidates
    case 0:
      return [
        "dateApplied",
        "source",
        "rating",
        "label",
        "scheduledBy",
        "dateInvited",
        "scoreRangeFilters",
      ];
    // inProgress candidates
    case 1:
      return [
        "dateApplied",
        "source",
        "rating",
        "label",
        "scheduledBy",
        "dateInvited",
        "dateStarted",
        "scoreRangeFilters",
        "advancedFilters",
      ];
    // completed candidates
    default:
      return [
        "dateApplied",
        "source",
        "rating",
        "label",
        "scheduledBy",
        "dateCompleted",
        "scoreRangeFilters",
        "advancedFilters",
        "archiveReason",
      ];
  }
};
const determineFilterPassed = (
  candidate,
  filterOption,
  filterParameter,
  stageIdToStageNameMap,
  scoreRanges,
  isAU,
  fromResultsV2
) => {
  const testEvents = getTestEvents(candidate, fromResultsV2);

  switch (filterOption) {
    case "dateApplied": {
      const dateToCheck = findEventDate(
        candidate,
        "earliestEventCompleted",
        isAU,
        fromResultsV2
      );
      return (
        dateToCheck !== null &&
        dateToCheck >= getNewDate(filterParameter.from).setHours(0, 0, 0, 0) &&
        dateToCheck <= getNewDate(filterParameter.to).setHours(23, 59, 59, 999)
      );
    }
    case "dateInvited":
      return testEvents
        .valueSeq()
        .reduce(
          (passed, testEvent) =>
            passed ||
            (testEvent.get("directLink") !== 1 &&
              getNewDate(
                convertDateTimezone(testEvent.get("dateEventAdded"), isAU)
              ) >= getNewDate(filterParameter.from).setHours(0, 0, 0, 0) &&
              getNewDate(
                convertDateTimezone(testEvent.get("dateEventAdded"), isAU)
              ) <= getNewDate(filterParameter.to).setHours(23, 59, 59, 999)),
          false
        );
    case "dateStarted":
      return testEvents
        .valueSeq()
        .reduce(
          (passed, testEvent) =>
            passed ||
            (getNewDate(
              convertDateTimezone(testEvent.get("trueDateEventStarted"), isAU)
            ) >= getNewDate(filterParameter.from).setHours(0, 0, 0, 0) &&
              getNewDate(
                convertDateTimezone(testEvent.get("trueDateEventStarted"), isAU)
              ) <= getNewDate(filterParameter.to).setHours(23, 59, 59, 999)),
          false
        );
    case "dateCompleted": {
      const dateToCheck = findEventDate(candidate, 2, isAU, fromResultsV2);
      return (
        dateToCheck >= getNewDate(filterParameter.from).setHours(0, 0, 0, 0) &&
        dateToCheck <= getNewDate(filterParameter.to).setHours(23, 59, 59, 999)
      );
    }
    case "source":
      return candidate.get("sourceName") === filterParameter;
    case "rating":
      return determineRatingFilterMet(
        candidate.get("averageRating"),
        filterParameter
      );
    case "label":
      return (
        candidate.get("labels") &&
        candidate
          .get("labels")
          .keySeq()
          .reduce(
            (labelIdFound, labelId) =>
              labelIdFound || String(labelId) === String(filterParameter),
            false
          )
      );
    case "scheduledBy":
      return testEvents.reduce(
        (scheduledByMatch, testEvent) =>
          scheduledByMatch ||
          filterParameter
            .map((user) => String(user.value))
            .includes(String(testEvent.get("scheduledBy"))),
        false
      );
    case "archiveReason":
      if (filterParameter === "disqualified") {
        return !!candidate.get("dqFilterId");
      }
      if (filterParameter === "rejected") {
        return (
          stageIdToStageNameMap[candidate.get("pipelineStageMapId")] ===
            "Rejected" && !candidate.get("dqFilterId")
        );
      }
      if (filterParameter === "onHold") {
        return (
          stageIdToStageNameMap[candidate.get("pipelineStageMapId")] ===
            "On Hold" && !candidate.get("dqFilterId")
        );
      }
      if (filterParameter === "withdrawn") {
        return (
          stageIdToStageNameMap[candidate.get("pipelineStageMapId")] ===
            "Withdrawn" && !candidate.get("dqFilterId")
        );
      }
      return "";
    case "scoreRangeFilters": {
      let passed = true;
      filterParameter.forEach((testIdScoreRangeIdsStr) => {
        const ids = testIdScoreRangeIdsStr.split("_");
        const testId = ids.splice(0, 1)[0];
        const testRanges = scoreRanges
          .get(testId)
          .reduce((matchedRanges, range) => {
            if (ids.includes(`${range.get("scoreRangeId")}`)) {
              return [...matchedRanges, range];
            }
            return matchedRanges;
          }, []);
        testRanges.forEach((range) => {
          if (
            !determineCandidatePassesTestRange(
              candidate,
              range,
              testId,
              fromResultsV2
            )
          ) {
            // here null? needs to pass one?
            passed = false;
          }
        });
      });
      return passed;
    }
    case "advancedFilters": {
      /**
       * Advanced Filters Logic
       * ---------------------
       * Overall principle: A candidate passes ONLY if ALL filters are passed.
       * If ANY filter fails, the entire filter fails (logical AND relationship).
       *
       * The logic uses a 'fail-fast' approach with these characteristics:
       * 1. 'passed' starts as null (undetermined)
       * 2. Once 'passed' is set to false, no subsequent filter can set it back to true
       * 3. Each filter only sets 'passed' to true if its criteria are met AND passed isn't already false
       */

      // Start with null (undetermined) rather than false to distinguish between "failed" and "not evaluated"
      let passed = null;
      /**
       * Ranking Score Filter
       * ------------------
       * Check if candidate's ranking score falls within the specified range.
       * Filter passes if: score >= minimum AND score <= maximum
       */
      if (filterParameter.rankingScoreRange !== null) {
        const rankingScoreValue = filterParameter.rankingScoreRange.value;
        const candidateRankingScore = candidate.get("rankingScore");
        if (rankingScoreValue !== null && candidateRankingScore !== null) {
          if (
            candidateRankingScore >= rankingScoreValue[0] &&
            candidateRankingScore <= rankingScoreValue[1]
          ) {
            passed = true;
          } else {
            passed = false;
          }
        }
      }
      /**
       * Proctoring Score Filter
       * --------------------
       * Checks if ALL of a candidate's proctoring results match the acceptable values.
       * The 'passed !== false' check ensures that if any previous filter failed,
       * this result can't override that failure.
       */
      if (filterParameter.proctoringScoreRange) {
        const proctoringScoreValue = filterParameter.proctoringScoreRange.value;
        const candidateProctoringResults = candidate?.getIn([
          "testEvents",
          "proctorings",
        ]);

        // Only evaluate if candidate has proctoring results
        if (candidateProctoringResults && candidateProctoringResults.size > 0) {
          candidateProctoringResults.forEach((testEvent) => {
            const candidateProctoringScore = testEvent.get("overallResult");
            // For each proctoring result:
            // 1. It must be in the acceptable values list AND
            // 2. No previous result or filter has already failed
            if (
              proctoringScoreValue.includes(candidateProctoringScore) &&
              passed !== false
            ) {
              passed = true;
            } else {
              passed = false;
            }
          });
        }
      }

      /**
       * Custom Test Range Filters
       * ----------------------
       * Process any remaining filters that are custom test ranges.
       * For each test, determine if the candidate passes its specified ranges.
       */
      Object.keys(filterParameter).forEach((testId) => {
        if (
          testId !== "rankingScoreRange" &&
          testId !== "proctoringScoreRange"
        ) {
          const testRanges = filterParameter[testId];
          const hasRange = Object.keys(testRanges).reduce(
            (hasRangeDefined, traitIndex) =>
              hasRangeDefined || !!testRanges[traitIndex].scoreRange,
            false
          );
          // Only evaluate tests that have at least one defined score range
          if (hasRange) {
            const rangePass = determineCandidatePassesCustomRange(
              candidate,
              testRanges,
              testId,
              fromResultsV2
            );
            // Handle test range results:
            // - false: candidate took test but failed the range criteria
            // - null: candidate did not take a test that has range criteria defined
            // - true: candidate took test and passed the range criteria
            // Treat both false and null as failures (candidate must take and pass required tests)
            if (rangePass === false || rangePass === null) {
              passed = false;
            } else if (rangePass && passed !== false) {
              passed = true;
            }
          }
        }
      });
      return passed;
    }
    default:
      return true;
  }
};
const determineRatingFilterMet = (avgRating, ratingCriteria) => {
  const ratingSystem = ratingCriteria.split("-")[0];
  const ratingThreshold = ratingCriteria.split("-")[1];
  if (!avgRating) {
    return false;
  }
  switch (ratingSystem) {
    case "tens":
      return avgRating && Number(avgRating) >= Number(ratingThreshold);
    case "stars":
      return avgRating && Number(avgRating) >= Number(ratingThreshold);
    case "thumbs":
      return avgRating.get &&
        (!isNaN(Number(avgRating.get("up"))) ||
          !isNaN(Number(avgRating.get("down"))))
        ? Number(avgRating.get("up") || 0) /
            (Number(avgRating.get("up") || 0) +
              Number(avgRating.get("down") || 0)) >=
            Number(ratingThreshold)
        : false;
    default:
      return false;
  }
};

const determineTestScorePassesRange = (
  passesRange,
  test,
  traitAbbrev,
  testId,
  minScoreRange,
  maxScoreRange,
  isDefaultRange = false
) => {
  let passesRangeToReturn = passesRange;

  const testField = traitAbbrevToTestField(
    traitAbbrev,
    testId,
    test.get("isTestMaker")
  );

  const score = test.get(testField);

  let numberedScore = Number(score);

  if (isNaN(score)) {
    if (testField.includes("CEFR")) {
      // if CEFR score (CLPT assessments), convert score to numeric equivalent
      // ex. "A1" -> 1 ... "C2" -> 6
      numberedScore = numericCefrScoreMap[score];
    } else if (
      rankingOrRecommendationObj[testId] &&
      rankingOrRecommendationObj[testId][score]
    ) {
      numberedScore = rankingOrRecommendationObj[testId][score];
    }
  }

  const testObjTestId = String(test.get("testId"));

  if (
    [
      testObjTestId,
      `TM${testObjTestId}`,
      `tm${testObjTestId}`,
      `${testObjTestId}_express`,
    ].includes(testId)
  ) {
    if (
      score === null ||
      numberedScore < Number(minScoreRange) ||
      (numberedScore > Number(maxScoreRange) && !isDefaultRange)
    ) {
      passesRangeToReturn = false;
    } else {
      passesRangeToReturn = true;
    }
  }
  return passesRangeToReturn;
};

const determineCandidatePassesCustomRange = (
  candidate,
  customTestRanges,
  testId,
  fromResultsV2
) => {
  let passesRange = null; // null = range test not taken by candidate, false = range test taken and failed, true = range test taken and passed
  // TODO: there are optimizations to be done here (fail fast)
  const testEvents = getTestEvents(candidate, fromResultsV2);

  testEvents.forEach((testEvent) => {
    const tests = testEvent.get("tests");
    if (tests) {
      tests.forEach((test) => {
        Object.keys(customTestRanges).forEach((traitIndex) => {
          const range = customTestRanges[traitIndex];
          // If the passesRange was already set to false, no need to continue since we already know the candidate is not passing range
          if (passesRange === false) {
            return;
          }
          if (range.scoreRange) {
            const traitAbbrev = changeCPIConToConsc(
              traitToTraitAbbrev(range.firstDropdown, range.secondDropdown),
              testId
            );

            const [min, max] = range.scoreRange;

            passesRange = determineTestScorePassesRange(
              passesRange,
              test,
              traitAbbrev,
              testId,
              min,
              max
            );
          }
        });
      });
    }
  });

  return passesRange;
};
const determineCandidatePassesTestRange = (
  candidate,
  testRange,
  testId,
  fromResultsV2
) => {
  let passesRange = null; // null = range test not taken by candidate, false = range test taken and failed, true = range test taken and passed

  const testEvents = getTestEvents(candidate, fromResultsV2);
  testEvents.forEach((testEvent) => {
    const tests = testEvent.get("tests");
    if (tests) {
      tests.forEach((test) => {
        const traitAbbrev = testRange.get("traitAbbrev");
        passesRange = determineTestScorePassesRange(
          passesRange,
          test,
          traitAbbrev,
          testId,
          testRange.get("min_scoreRange"),
          testRange.get("max_scoreRange"),
          testRange.get("isDefault")
        );
      });
    }
  });

  return passesRange;
};

export const determineScoreRangeTraitMet = (
  test,
  testId,
  scoreRangeTrait,
  traitName
) => {
  let testScore = test.get(traitName);
  if (testScore === null || testScore === undefined) {
    return false;
  }

  const useTextScore =
    testScoreRangeHasTextScores(testId, scoreRangeTrait.get("rangeName")) &&
    typeof testScore === "string";

  if (useTextScore) {
    testScore = rankingOrRecommendationScore(testId, testScore);
  }

  const isCriteriaScoreRange = scoreRangeTrait.get("isDefault");
  return !(
    Number(testScore) < Number(scoreRangeTrait.get("min_scoreRange")) ||
    (!isCriteriaScoreRange &&
      Number(testScore) > Number(scoreRangeTrait.get("max_scoreRange")))
  );
};

export const determineTestScoreRangesMet = (
  test,
  jobScoreRanges,
  testTrait = null
) => {
  if (!test || test.size < 1 || !jobScoreRanges || jobScoreRanges.size < 1) {
    return false;
  }

  const testScoreRangeInfo = {
    testHasScoreRanges: true, // whether the test has any score ranges applied
    metTestScoreRanges: true, // whether candidate met all score ranges for the test
    metTraitScoreRange: false, // whether candidate met score range for a specific test trait; only relevant if testTrait passed in
  };

  const testId = Number(test.get("testId"));
  const testScoreRanges =
    jobScoreRanges.get(String(testId)) || jobScoreRanges.get(`tm${testId}`);

  if (!testScoreRanges || testScoreRanges.size < 1) {
    testScoreRangeInfo.metTestScoreRanges = false;
    testScoreRangeInfo.testHasScoreRanges = false;
    return testScoreRangeInfo;
  }

  // testScoreRanges is a List of Maps where each Map is a trait being evaluated in the score range.
  testScoreRanges.forEach((scoreRangeTrait) => {
    const traitName = scoreRangeTrait.get("traitAbbrev")
      ? traitAbbrevToTestField(scoreRangeTrait.get("traitAbbrev"), testId)
      : "rawScore";

    const traitMet = determineScoreRangeTraitMet(
      test,
      testId,
      scoreRangeTrait,
      traitName
    );

    // if any trait score is not in range, the test did not meet all associated score ranges
    if (!traitMet) {
      testScoreRangeInfo.metTestScoreRanges = false;
    }

    // only change the metTraitScoreRange value if a testTrait was passed in, matches the traitName, and
    // the trait score is within the trait range
    if (testTrait && testTrait === traitName && traitMet) {
      testScoreRangeInfo.metTraitScoreRange = true;
    }
  });
  return testScoreRangeInfo;
};

export const candidateStatusMap = {
  0: "invited",
  1: "inProgress",
  2: "completed",
};

export const traitAbbrevToTestFieldObj = {
  10: {
    raw: "rawScore",
    percentile: "percentile",
    verbal: "verbalPercentile",
    math: "mathPercentile",
    spatial: "spatialPercentile",
  },
  12: {
    raw: "rawScore",
    percentile: "percentile",
    math: "mathCorrect",
    verbal: "verbalCorrect",
  },
  13: {
    AGREE: "agreeableness",
    CONSC: "conscientiousness",
    EXT: "extraversion",
    OPEN: "openness",
    STAB: "stressTolerance",
  },
  14: {
    percentile: "overallPercentile",
    ATT: "attention",
    MEM: "memory",
    REA: "reasoning",
    VIG: "vigilance",
    FIL: "filtering",
    VWM: "verbalWorkingMemory",
    SWM: "spatialWorkingMemory",
    VIS: "visualization",
    LOG: "logic",
    IO: "informationOrdering",
    PRT: "perceptualReaction",
    DA: "dividedAttention",
  },
  15: {
    raw: "rawScore",
    SAL: "SAL_adjPercentile",
    CC: "CC_adjPercentile",
    CLS: "CLS_adjPercentile",
    ACH: "ACH_adjPercentile",
    MOT: "MOT_adjPercentile",
    CMP: "CMP_adjPercentile",
    GO: "GO_adjPercentile",
    PLN: "PLN_adjPercentile",
    INI: "INI_adjPercentile",
    TMP: "TMP_adjPercentile",
    MGT: "MGT_adjPercentile",
    AST: "AST_adjPercentile",
    PDL: "PDL_adjPercentile",
    EXT: "EXT_adjPercentile",
    COP: "COP_adjPercentile",
    RLX: "RLX_adjPercentile",
    PAT: "PAT_adjPercentile",
    SCN: "SCN_adjPercentile",
  },
  16: { raw: "rawScore", percentile: "percentile" },
  17: { raw: "rawScore", percentile: "percentile" },
  18: { raw: "wpm" },
  19: { raw: "rawScore", percentile: "percentile" },
  20: { raw: "rawScore", percentile: "percentile" },
  21: { raw: "rawScore", percentile: "percentile" },
  22: { raw: "rawScore", percentile: "percentile" },
  23: {
    percentile: "overallPercentile",
    DA: "dividedAttention",
    VIG: "vigilance",
    FIL: "filtering",
    PRT: "perceptualReaction",
  },
  24: {
    raw: "rawScore",
    SAL: "SAL_adjPercentile",
    CC: "CC_adjPercentile",
    CLS: "CLS_adjPercentile",
    ACH: "ACH_adjPercentile",
    MOT: "MOT_adjPercentile",
    CMP: "CMP_adjPercentile",
    GO: "GO_adjPercentile",
    PLN: "PLN_adjPercentile",
    INI: "INI_adjPercentile",
    TMP: "TMP_adjPercentile",
    MGT: "MGT_adjPercentile",
    AST: "AST_adjPercentile",
    PDL: "PDL_adjPercentile",
    EXT: "EXT_adjPercentile",
    COP: "COP_adjPercentile",
    RLX: "RLX_adjPercentile",
    PAT: "PAT_adjPercentile",
    SCN: "SCN_adjPercentile",
  },
  29: {
    ACH: "EPPAchievement",
    MOT: "EPPMotivation",
    CMP: "EPPCompetitiveness",
    MGT: "EPPManagerial",
    AST: "EPPAssertiveness",
    EXT: "EPPExtroversion",
    COP: "EPPCooperativeness",
    PAT: "EPPPatience",
    SCN: "EPPSelfConfidence",
    CON: "EPPConscientiousness",
  },
  30: { raw: "rawScore", ranking: "CLIKRanking" },
  31: {
    raw: "rawScore",
    CON: "CON_percentile",
    PER: "PER_percentile",
    HON: "HON_percentile",
    THEFT: "THEFT_percentile",
  },
  32: { raw: "rawScore", percentile: "percentile" },
  33: { raw: "rawScore", percentile: "percentile" },
  34: {
    ACH: "EPPAchievement",
    MOT: "EPPMotivation",
    CMP: "EPPCompetitiveness",
    MGT: "EPPManagerial",
    AST: "EPPAssertiveness",
    EXT: "EPPExtroversion",
    COP: "EPPCooperativeness",
    PAT: "EPPPatience",
    SCN: "EPPSelfConfidence",
    CON: "EPPConscientiousness",
    OPEN: "EPPOpenness",
    STAB: "EPPStressTolerance",
    Manager: "EPPJobMatch:Manager",
    AccountingFinance: "EPPJobMatch:Accounting/Finance",
    Administrative_Assistant: "EPPJobMatch:AdministrativeAsst",
    Analyst: "EPPJobMatch:Analyst",
    Bank_Teller: "EPPJobMatch:BankTeller",
    Collections: "EPPJobMatch:Collections",
    Customer_Service: "EPPJobMatch:CustomerService",
    Front_DeskReception: "EPPJobMatch:FrontDesk/Reception",
    Medical_Assistant: "EPPJobMatch:MedicalAssistant",
    ProductionManufacturing: "EPPJobMatch:Production/Manufacturing",
    ProgrammerDeveloper: "EPPJobMatch:Programmer/Developer",
    Sales: "EPPJobMatch:Sales",
  },
  35: { raw: "rawScore", percentile: "percentile" },
  36: { raw: "rawScore", percentile: "percentile" },
  37: {
    raw: "rawScore",
    percentile: "percentile",
    logic: "logicPercentile",
    math: "mathPercentile",
    detail: "detailPercentile",
    spatial: "spatialPercentile",
  },
  38: { raw: "rawScore", percentile: "percentile" },
  39: { raw: "rawScore", percentile: "percentile" },
  40: { raw: "rawScore", percentile: "percentile" },
  41: { raw: "rawScore", percentile: "percentile" },
  42: {
    percentile: "percentile",
    detail: "detailPercentile",
    verbal: "verbalPercentile",
    math: "mathPercentile",
  },
  43: { raw: "rawScore", percentile: "percentile" },
  44: {
    percentile: "percentile",
    perceiving: "perceivingPercentile",
    understand: "understandPercentile",
    raw: "raw",
  },
  45: {
    percentile: "percentile",
    problemSolving: "problemSolvingPercentile",
    numericalReasoning: "numericalReasoningPercentile",
    verbalKnowledge: "verbalKnowledgePercentile",
  },
  46: {
    percentile: "percentile",
    safety: "safetyPercentileScore",
    risk: "riskPercentileScore",
    stress: "stressPercentileScore",
    drug: "drugPercentileScore",
    violence: "violencePercentileScore",
    ENH: "validityENHPercentile",
    INC: "validityINCScore",
  },
  48: {
    percentile: "percentile",
  },
  49: { raw: "rawScore", ranking: "CLIKRanking" },
  50: {
    percentile: "percentile",
  },
  51: {
    percentile: "percentile",
    perceiving: "perceivingPercentile",
    understand: "understandPercentile",
    managing: "managingPercentile",
    raw: "raw",
  },
  52: {
    percentile: "percentile",
    problemSolving: "problemSolvingPercentile",
    numericalReasoning: "numericalReasoningPercentile",
  },
  53: { score: "score", proficiency: "proficiency" },
  54: { score: "score", proficiency: "proficiency" },
  55: {
    score: "score",
    proficiency: "proficiency",
  },
  57: {
    score: "stwenty",
    proficiency: "proficiency",
    readingScore: "readingScore",
    readingProficiency: "readingProficiency",
    readingCEFR: "readingCEFR",
    listeningScore: "listeningScore",
    listeningProficiency: "listeningProficiency",
    listeningCEFR: "listeningCEFR",
    writingProficiency: "writingProficiency",
    writingCEFR: "writingCEFR",
  },
  "57_express": {
    score: "stwenty",
    proficiency: "proficiency",
  },
  59: {
    percentile: "percentile",
  },
  // LAST LINE - DO NOT MOVE THIS COMMENT traitAbbrevToTestFieldObj
  TM: { raw: "rawScore" },
};

export const traitAbbrevToTestField = (traitAbbrev, testId, isTestMaker) => {
  if (!traitAbbrev || isTestMaker === 1 || isTestMaker === "1") {
    return "rawScore";
  }
  if (
    traitAbbrevToTestFieldObj[testId] &&
    traitAbbrevToTestFieldObj[testId][traitAbbrev]
  ) {
    return traitAbbrevToTestFieldObj[testId][traitAbbrev];
  }
  switch (traitAbbrev) {
    case "raw":
      return "rawScore";
    default:
      return traitAbbrev;
  }
};
const scoresToShowObj = {
  CCAT: ["rawScore", "Raw Score", "percentile", "Percentile"],
  CBST: ["rawScore", "Raw Score", "percentile", "Percentile"],
  WTMA: ["rawScore", "Raw Score", "percentile", "Percentile"],
  UCAT: ["rawScore", "Raw Score", "percentile", "Percentile"],
  Word03: ["rawScore", "Raw Score", "percentile", "Percentile"],
  Word07: ["rawScore", "Raw Score", "percentile", "Percentile"],
  Word10: ["rawScore", "Raw Score", "percentile", "Percentile"],
  Word13: ["rawScore", "Raw Score", "percentile", "Percentile"],
  Excel03: ["rawScore", "Raw Score", "percentile", "Percentile"],
  Excel07: ["rawScore", "Raw Score", "percentile", "Percentile"],
  Excel10: ["rawScore", "Raw Score", "percentile", "Percentile"],
  Excel13: ["rawScore", "Raw Score", "percentile", "Percentile"],
  PPT13: ["rawScore", "Raw Score", "percentile", "Percentile"],
  TT: ["rawScore", "Adj WPM", "percentile", "Percentile"],
  TKT: ["wpm", "KPH", "percentile", "Percentile"],
  MRAB: ["overallPercentile", "Percentile", null, null],
  CAST: ["overallPercentile", "Percentile", null, null],
  EPP: ["EPPPositionMatch", "% Match", null, null],
  CSAP: ["CSAPRanking", "", null, null],
  SalesAP: ["salesAPRanking", "", null, null],
  WPP: ["rawScore", "", null, null],
  CLIK: ["CLIKRanking", "", null, null],
  CPI: [null, null, null, null],
  GAME: ["percentile", "Percentile", null, null],
  CMRA: ["rawScore", "Raw Score", "percentile", "Percentile"],
  Emotify: ["percentile", "Percentile", null, null],
  Cognify: ["percentile", "Percentile", null, null],
  Safety: ["percentile", "Percentile", null, null],
  UCognify: ["percentile", "Percentile", null, null],
  Illustrait: ["percentile", "Percentile", null, null],
  WAA: ["percentile", null, null, null],
  "Excel 365": ["score", "Score", "proficiency", "Proficiency"],
  "Word 365": ["score", "Score", "proficiency", "Proficiency"],
  "PowerPoint 365": ["score", "Score", "proficiency", "Proficiency"],
  "Criteria Language Proficiency Test - English": [
    "stwenty",
    "Overall Score",
    "proficiency",
    "Proficiency",
  ],
  CTRSE: ["percentile", "Percentile", null, null],
  // LAST LINE - DO NOT MOVE THIS COMMENT scoresToShowObj
};

/*
 *  Purpose:  To determine which scores are shown in score boxes. Many tests have multiple score results, and this
 *             checks the scoresToShowObj to determine which ones to display.
 *  Parameters:
 *    testName:  the name of the test of which scores need to be displayed
 *  Returns: an Array of Strings containing the scores to be displayed.
 *
 */
export const determineScoresToShow = (testName) =>
  scoresToShowObj[testName] || [
    "rawScore",
    "Raw Score",
    "percentile",
    "Percentile",
  ];

export const getTestTextScoreNames = (testId) => {
  switch (testId) {
    case "15":
    case "24":
      return ["raw"];
    case "31":
      return ["rawScore"];
    case "30":
    case "49":
      return ["ranking"];
    case "53":
    case "54":
    case "55":
      return ["proficiency"];
    case "57":
      return [
        "proficiency",
        "readingProficiency",
        "listeningProficiency",
        "writingProficiency",
      ];
    default:
      return [];
  }
};

export const getScoreDisplay = (score, scoreName, testId) => {
  const isTextScore = getTestTextScoreNames(testId).includes(scoreName);

  const shouldConvertScoreToText = isTextScore && !isNaN(Number(score));

  return shouldConvertScoreToText
    ? rankingOrRecommendationScore(testId, score)
    : score;
};

export const getScoresOutput = (test) => {
  const scoresToShow = determineScoresToShow(test.get("testName"));
  const testId = String(test.get("testId"));
  const isTextScoreTest = rankingOrRecommendationTests.includes(testId);

  const [firstScoreName, firstScoreLabel, secondScoreName, secondScoreLabel] =
    scoresToShow;

  const firstScore = test.get(firstScoreName);
  const secondScore = test.get(secondScoreName);

  let scoresOutput = null;

  // we have to display a custom message if the test is Illustrait and has a null percentile result
  if (testId === "48" && firstScore === null) {
    scoresOutput = (
      <div>
        <div className="test-card-illustrait-work-styles-only">
          Work Styles Only
        </div>
      </div>
    );
  } else if (secondScoreLabel === null) {
    scoresOutput = (
      <div>
        {/* sometimes the scores for recommendation come in as 1, 2, 3 other times it's not recommended, rec, and highly  */}
        <div className="test-card-score-name">
          {firstScoreLabel}{" "}
          <strong>{getScoreDisplay(firstScore, firstScoreName, testId)}</strong>
        </div>
      </div>
    );
  } else {
    scoresOutput = (
      <div>
        <strong>{firstScore}</strong> (
        {isTextScoreTest ? null : `${secondScoreLabel} `}
        <strong>{getScoreDisplay(secondScore, secondScoreName, testId)}</strong>
        )
      </div>
    );
  }
  return scoresOutput;
};

export const subTraitAbbrevToSubTraitPretty = {
  verbal: "Verbal Ability",
  spatial: "Spatial Reasoning",
  math: "Math & Logic",
  // math: 'Numerical Reasoning', // for UCAT and GAME - will show Math & Logic, not Numerical Reasoning
  logic: "Logic & Analysis",
  detail: "Attention to Detail",
  AGREE: "Agreeableness",
  CON: "Conscientiousness",
  CONSC: "Conscientiousness",
  EXTRA: "Extraversion",
  OPEN: "Openness",
  STAB: "Stress Tolerance",
  ATT: "Attention",
  MEM: "Memory",
  REA: "Reasoning",
  DA: "Divided Attention",
  VIG: "Selective Attention: Vigilance",
  FIL: "Selective Attention: Filtering",
  VWM: "Verbal Working Memory",
  SWM: "Spatial Working Memory",
  VIS: "Visualization",
  LOG: "Logic",
  IO: "Information Ordering",
  PRT: "Perceptual Reaction Time",
  SAL: "Sales Disposition",
  CC: "Initiative - Cold Calling",
  CLS: "Sales Closing",
  ACH: "Achievement",
  CMP: "Competitiveness",
  MOT: "Motivation",
  GO: "Goal Orientation",
  PLN: "Planning",
  INI: "Initiative - General",
  TMP: "Team Player",
  MGT: "Managerial",
  AST: "Assertiveness",
  PDL: "Personal Diplomacy",
  EXT: "Extroversion",
  COP: "Cooperativeness",
  RLX: "Relaxed Style",
  PAT: "Patience",
  SCN: "Self-Confidence",
  AccountingFinance: "Accounting and Finance",
  Administrative_Assistant: "Administrative and Clerical",
  Analyst: "Analysis, Planning and Consulting",
  Bank_Teller: "Tellers and Clerical Finance",
  Collections: "Account Collection and Receivables",
  Customer_Service: "Client and Customer Service",
  Front_DeskReception: "Front Desk and Reception",
  Manager: "Management and Leadership",
  Medical_Assistant: "Healthcare Practice and Support",
  ProductionManufacturing: "Production and Manufacturing",
  ProgrammerDeveloper: "Software Engineering and Development",
  Sales: "Sales and Business Development",
  PER: "Perseverance",
  HON: "Integrity/Honesty",
  THEFT: "Attitudes Towards Theft",
  perceivingPercentile: "Perceiving Emotions",
  understandPercentile: "Understanding Emotions",
  percentile: "Overall",
  problemSolvingPercentile: "Problem Solving",
  numericalReasoningPercentile: "Numerical Reasoning",
  verbalKnowledgePercentile: "Verbal Knowledge",
  safetyPercentileScore: "Safety Control",
  riskPercentileScore: "Risk Aversion",
  stressPercentileScore: "Stress Management",
  drugPercentileScore: "Drug Aversion",
  violencePercentileScore: "Attitudes Towards Violence",
  managingPercentile: "Managing Emotions",
};

const subTraitToSubTraitAbbrev = (subTrait) => {
  switch (subTrait) {
    case "Verbal Ability":
    case "Verbal":
      return "verbal";
    case "Spatial Reasoning":
      return "spatial";
    case "Math & Logic":
    case "Math":
    case "Numerical Reasoning":
      return "math";
    case "Logic & Analysis":
      return "logic";
    case "Attention to Detail":
      return "detail";
    case "Agreeableness":
      return "AGREE";
    case "Conscientiousness":
      return "CON";
    case "Extraversion":
      return "EXTRA";
    case "Openness":
      return "OPEN";
    case "Stress Tolerance":
      return "STAB";
    case "Attention":
      return "ATT";
    case "Memory":
      return "MEM";
    case "Reasoning":
      return "REA";
    case "Divided Attention":
      return "DA";
    case "Selective Attention: Vigilance":
      return "VIG";
    case "Selective Attention: Filtering":
      return "FIL";
    case "Verbal Working Memory":
      return "VWM";
    case "Spatial Working Memory":
      return "SWM";
    case "Visualization":
      return "VIS";
    case "Logic":
      return "LOG";
    case "Information Ordering":
      return "IO";
    case "Perceptual Reaction Time":
      return "PRT";
    case "Sales Disposition":
      return "SAL";
    case "Initiative - Cold Calling":
      return "CC";
    case "Sales Closing":
      return "CLS";
    case "Achievement":
      return "ACH";
    case "Competitiveness":
      return "CMP";
    case "Motivation":
      return "MOT";
    case "Goal Orientation":
      return "GO";
    case "Planning":
      return "PLN";
    case "Initiative - General":
      return "INI";
    case "Team Player":
      return "TMP";
    case "Managerial":
      return "MGT";
    case "Assertiveness":
      return "AST";
    case "Personal Diplomacy":
      return "PDL";
    case "Extroversion":
      return "EXT";
    case "Cooperativeness":
      return "COP";
    case "Relaxed Style":
      return "RLX";
    case "Patience":
      return "PAT";
    case "Self-Confidence":
      return "SCN";
    case "Accounting and Finance":
      return "AccountingFinance";
    case "Administrative and Clerical":
      return "Administrative_Assistant";
    case "Analysis, Planning and Consulting":
      return "Analyst";
    case "Tellers and Clerical Finance":
      return "Bank_Teller";
    case "Account Collection and Receivables":
      return "Collections";
    case "Client and Customer Service":
      return "Customer_Service";
    case "Front Desk and Reception":
      return "Front_DeskReception";
    case "Management and Leadership":
      return "Manager";
    case "Healthcare Practice and Support":
      return "Medical_Assistant";
    case "Production and Manufacturing":
      return "ProductionManufacturing";
    case "Software Engineering and Development":
      return "ProgrammerDeveloper";
    case "Sales and Business Development":
      return "Sales";
    case "Perseverance":
      return "PER";
    case "Integrity/Honesty":
      return "HON";
    case "Attitudes Towards Theft":
      return "THEFT";
    case "Perceiving Emotions":
      return "perceivingPercentile";
    case "Understanding Emotions":
      return "understandPercentile";
    case "Managing Emotions":
      return "managingPercentile";
    case "Problem Solving":
      return "problemSolvingPercentile";
    // case 'Numerical Reasoning':
    //   return 'numericalReasoningPercentile';
    case "Verbal Knowledge":
      return "verbalKnowledgePercentile";
    case "Safety Control":
      return "safety";
    case "Risk Aversion":
      return "risk";
    case "Stress Management":
      return "stress";
    case "Drug Aversion":
      return "drug";
    case "Attitudes Towards Violence":
      return "violence";
    case null:
      return alert("Something went wrong! Please refresh."); // eslint-disable-line no-alert
    default:
      return "TM";
  }
};
const traitToTraitAbbrevObj = {
  "By Raw Score": "raw",
  "By Adjusted WPM": "raw",
  "By Recommendation": "raw",
  "By Rating": "raw",
  "By KPH": "raw",
  "By Overall Percentile": "percentile",
  "By Proficiency (Highly Proficient, Proficient, Not Proficient)": "ranking",
  "By % Match": "percentile",
  "By Score": "score",
  "By Proficiency (Beginner, Foundational, Intermediate, Skilled, Advanced)":
    "proficiency",
  "By Reading Score": "readingScore",
  "By Listening Score": "listeningScore",
  "By Writing Score": "writingScore",
  "By Reading Proficiency": "readingProficiency",
  "By Listening Proficiency": "listeningProficiency",
  "By Writing Proficiency": "writingProficiency",
  "By Reading CEFR": "readingCEFR",
  "By Listening CEFR": "listeningCEFR",
  "By Writing CEFR": "writingCEFR",
};
export const traitToTraitAbbrev = (trait, subTrait) => {
  switch (trait) {
    case "By Raw Score":
    case "By Adjusted WPM":
    case "By Recommendation":
    case "By Rating":
    case "By Overall Percentile":
    case "By Proficiency (Highly Proficient, Proficient, Not Proficient)":
    case "By KPH":
    case "By % Match":
    case "By Score":
    case "By Proficiency (Beginner, Foundational, Intermediate, Skilled, Advanced)":
    case "By Reading Score":
    case "By Listening Score":
    case "By Writing Score":
    case "By Reading Proficiency":
    case "By Listening Proficiency":
    case "By Writing Proficiency":
    case "By Reading CEFR":
    case "By Listening CEFR":
    case "By Writing CEFR":
      return traitToTraitAbbrevObj[trait];
    case "By Trait Percentile":
    case "By Traits Raw Score":
    case "By Job Family Match %":
      return subTraitToSubTraitAbbrev(subTrait);
    default:
      return null;
  }
};
export const changeCPIConToConsc = (traitAbbrev, testId) =>
  Number(testId) === 13 && traitAbbrev === "CON" ? "CONSC" : traitAbbrev;

export const rankingOrRecommendationObj = {
  15: {
    "Not Recommended": 1,
    Recommended: 2,
    "Highly Recommended": 3,
    1: "Not Recommended",
    2: "Recommended",
    3: "Highly Recommended",
  },
  24: {
    "Not Recommended": 1,
    Recommended: 2,
    "Highly Recommended": 3,
    1: "Not Recommended",
    2: "Recommended",
    3: "Highly Recommended",
  },
  30: {
    "Not Proficient": 1,
    Proficient: 2,
    "Highly Proficient": 3,
    1: "Not Proficient",
    2: "Proficient",
    3: "Highly Proficient",
    // Adding these lines for Analytics Candidate Insights Graph
    0: "Not Proficient",
    14: "Proficient",
    18: "Highly Proficient",
  },
  31: {
    Low: 1,
    Medium: 2,
    High: 3,
    1: "Low",
    2: "Medium",
    3: "High",
  },
  49: {
    "Not Proficient": 1,
    Proficient: 2,
    "Highly Proficient": 3,
    1: "Not Proficient",
    2: "Proficient",
    3: "Highly Proficient",
    // Adding these lines for Analytics Candidate Insights Graph
    0: "Not Proficient",
    17: "Proficient",
    22: "Highly Proficient",
  },
  53: {
    Beginner: 1,
    Foundational: 2,
    Intermediate: 3,
    Skilled: 4,
    Advanced: 5,
    1: "Beginner",
    2: "Foundational",
    3: "Intermediate",
    4: "Skilled",
    5: "Advanced",
  },
  54: {
    Beginner: 1,
    Foundational: 2,
    Intermediate: 3,
    Skilled: 4,
    Advanced: 5,
    1: "Beginner",
    2: "Foundational",
    3: "Intermediate",
    4: "Skilled",
    5: "Advanced",
  },
  55: {
    1: "Beginner",
    2: "Foundational",
    3: "Intermediate",
    4: "Skilled",
    5: "Advanced",
    Beginner: 1,
    Foundational: 2,
    Intermediate: 3,
    Skilled: 4,
    Advanced: 5,
  },
  57: {
    1: "Beginner",
    2: "Foundational",
    3: "Intermediate",
    4: "Skilled",
    5: "Advanced",
    Beginner: 1,
    Foundational: 2,
    Intermediate: 3,
    Skilled: 4,
    Advanced: 5,
  },
  "57_express": {
    1: "Beginner",
    2: "Foundational",
    3: "Intermediate",
    4: "Skilled",
    5: "Advanced",
    Beginner: 1,
    Foundational: 2,
    Intermediate: 3,
    Skilled: 4,
    Advanced: 5,
  },
  // LAST LINE - DO NOT MOVE THIS COMMENT rankingOrRecommendationObj
};

// return a String array of testIds
export const rankingOrRecommendationTests = Object.keys(
  rankingOrRecommendationObj
);

export const rankingOrRecommendationScore = (testId, score) =>
  rankingOrRecommendationObj[testId][score];

// CEFR scores (CLPT assessments) mapped to numeric scores for score range calculations
export const numericCefrScoreMap = {
  1: "A1",
  2: "A2",
  3: "B1",
  4: "B2",
  5: "C1",
  6: "C2",
  A1: 1,
  A2: 2,
  B1: 3,
  B2: 4,
  C1: 5,
  C2: 6,
};

export const candidateCardSource = {
  beginDrag({
    checkedCandidates,
    candidate,
    candidateId,
    currentStageId,
    allCandidates,
  }) {
    // If there are other cards that are checked move those cards as well (Bulk drag)
    const candidateIds = [...checkedCandidates];
    if (!candidateIds.includes(candidateId)) {
      candidateIds.push(candidateId);
    }
    const candidateName = candidate.get("testTakerFirstName");

    let draggedCandidateId = "";
    const candidatePrevStageMap = {};
    if (checkedCandidates.length > 0) {
      checkedCandidates.forEach((checkedCandidateId, index) => {
        if (index === 0) {
          draggedCandidateId = checkedCandidateId;
        }
        candidatePrevStageMap[checkedCandidateId] =
          allCandidates[checkedCandidateId].get("pipelineStageMapId");
      });
    } else {
      draggedCandidateId = candidateId;
    }

    return {
      candidateIds,
      candidateName,
      stageId:
        (currentStageId === "archive" || currentStageId === "all") &&
        checkedCandidates.length > 0
          ? candidatePrevStageMap
          : allCandidates[draggedCandidateId].get("pipelineStageMapId"),
    };
  },
  canDrag({ roles, userData }) {
    const moveEmailCandidatesPermission =
      roles.size > 0 && userData
        ? RolesAccessLibrary(
            roles,
            userData.roleId
          ).moveEmailCandidatesPermission()
        : null;
    return !!moveEmailCandidatesPermission;
  },
};

export const EPPtraits = [
  "AccountingFinance",
  "Administrative_Assistant",
  "Analyst",
  "Bank_Teller",
  "Collections",
  "Customer_Service",
  "Front_DeskReception",
  "Manager",
  "Medical_Assistant",
  "ProductionManufacturing",
  "ProgrammerDeveloper",
  "Sales",
];

export const ItemTypes = {
  CANDIDATE: "candidate",
};

export const microsoftTestIdToName = {
  19: "Excel 03",
  20: "Word 03",
  21: "Excel 07",
  22: "Word 07",
  32: "Excel 10",
  33: "Word 10",
  35: "Excel 13",
  36: "Word 13",
  38: "PPT 13",
  39: "Excel 16",
  40: "Word 16",
  41: "PPT 16",
  53: "Excel 365",
  54: "Word 365",
  55: "PowerPoint 365",
  // LAST LINE - DO NOT MOVE THIS COMMENT microsoftTestIdToName
};

export const fieldsToFilterOut = [
  "subTestId",
  "testId",
  "testName",
  "testEventId",
  "invalid",
  "EPPPositionMatch",
  "overallRawScore",
  "mrabTestId",
  "typingErrors",
  "CON_score",
  "PER_score",
  "HON_score",
  "THEFT_score",
  "FAKING_score",
  "FAKING_percentile",
];

/*
 * NO NEED TO ADD THE SUBTESTID MANUALLY!
 * Run `npm run generate` and select `newSubTest` to automate the adding new subTestId process
 * The script will automatically add the new subTestId to all the mappings being used in the repo
 * For more instructions: https://criteria.atlassian.net/wiki/spaces/FRO/pages/1562017793/Adding+SubTestIds+of+New+Assessments+in+Platform#MUST-READ%3A-Automating-the-Adding-SubTestId-Process-with-the-Plop-Generator
 */
export const subTestIdToSubTestName = {
  1: "CCAT",
  2: "CCAT",
  3: "CCAT",
  4: "CBST",
  5: "CBST",
  6: "CBST",
  7: "CPI",
  8: "BMST",
  9: "BVST",
  10: "MRAB",
  11: "MRAB",
  12: "MRABe",
  13: "CAST",
  14: "Memory",
  15: "Selective Attention",
  16: "Mental Rotation",
  17: "Divided Attention",
  18: "CASTe",
  19: "CSAP",
  20: "SalesAP",
  21: "WTMA",
  22: "WTMAes-la",
  23: "TT",
  24: "TKT",
  25: "Excel 03",
  26: "Word 03",
  27: "Excel 07",
  28: "Word 07",
  29: "CCAT",
  30: "EPP",
  31: "CCAT",
  32: "CBST",
  33: "CCAT",
  34: "CLIK",
  35: "WPP",
  36: "CCAT",
  37: "CCAT",
  38: "CCAT",
  39: "CBSTe",
  40: "CCAT",
  41: "Excel 10",
  42: "Word 10",
  43: "EPP",
  44: "CCAT Rx",
  45: "CCAT UK2",
  46: "CCAT",
  47: "CCAT",
  48: "CCAT",
  49: "CCAT",
  50: "CCAT",
  51: "CCAT",
  52: "WPPe",
  53: "CCAT",
  54: "CCAT",
  55: "CBST CA",
  56: "CCAT CA",
  57: "CCAT",
  58: "Excel 13",
  59: "Word 13",
  60: "CCAT",
  61: "CCAT",
  62: "CCAT",
  63: "CBST",
  64: "CBST",
  65: "CBST Rx",
  66: "AIT",
  67: "UCAT",
  68: "UCATe",
  69: "UCATf",
  70: "CBST EU",
  71: "CCAT UK2 RX",
  72: "PPT 13",
  73: "UCATc",
  74: "UCATj",
  75: "UCATh",
  76: "Excel 16",
  77: "Word 16",
  78: "PPT 16",
  79: "UCATg",
  80: "CBST",
  81: "CBST",
  82: "CBST",
  83: "CBST",
  84: "UCAT Rx",
  85: "UCATpl",
  86: "UCATpg",
  87: "UCATb",
  88: "EPP ES-ES",
  89: "EPPf",
  90: "EPPp",
  91: "EPPc",
  92: "GAME",
  93: "UCATr",
  94: "UCATa",
  95: "CMRA",
  96: "UCATsv",
  97: "UCATm",
  98: "EPPu",
  99: "EPPg",
  100: "WTMA Rx",
  101: "CCAT",
  102: "CMRAs",
  103: "CMRAf",
  104: "UCATcs",
  105: "UCATnl",
  106: "UCATu",
  109: "Emotify",
  110: "Cognify",
  111: "CBST Rx",
  112: "Emotify Rx",
  113: "CCAT",
  114: "CBST",
  115: "CMRA",
  116: "UCAT",
  117: "GAME",
  118: "UCATk",
  119: "WSP L",
  120: "WSP",
  121: "Cognify",
  122: "Cognify Rx",
  123: "Cognify Rx",
  124: "CCAT Rx",
  125: "CBST Rx",
  126: "CMRA Rx",
  127: "UCAT Rx",
  128: "Excel 16",
  129: "Word 16",
  130: "PPT 16",
  131: "CCAT cAT",
  132: "Illustrait",
  133: "UCATc",
  134: "UCATpg",
  135: "CLIK",
  136: "WAA",
  137: "Emotify",
  138: "UCognify",
  139: "UCognify",
  140: "UCognify de",
  141: "UCognify es-es",
  142: "UCATi",
  143: "UCognify pt-br",
  144: "UCognify pt-br",
  145: "UCognify fr-ca",
  146: "UCognify fr-ca",
  150: "UCognify pt-pt",
  151: "UCognify pt-pt",
  152: "EPP ES-LA",
  153: "UCognify es-la",
  154: "UCognify es-la",
  156: "EPP ZH-CN",
  157: "EPP JA",
  155: "EPP PT-BR",
  158: "UCognify de",
  159: "UCognify ko",
  160: "UCognify ko",
  161: "UCognify tr",
  162: "UCognify tr",
  163: "Cognify tr",
  164: "Cognify tr",
  165: "GAMEes-la",
  166: "UCognify fr-fr",
  167: "UCognify fr-fr",
  168: "UCognify es-es",
  169: "UCognify ja",
  170: "UCognify ja",
  171: "WSPes-la",
  172: "WSP L es-la",
  173: "UCognify zh-cn",
  174: "UCognify zh-cn",
  175: "WSPpt-br",
  176: "WSP L pt-br",
  177: "UCATfr-ca",
  178: "UCATes-la",
  179: "Excel 365",
  180: "Excel 365",
  181: "Word 365",
  182: "PowerPoint 365",
  183: "UCognify it",
  184: "UCognify it",
  185: "EPPit",
  186: "EPPfr-fr",
  187: "WTMAfr-ca",
  188: "RCAT",
  189: "RCAT",
  190: "CLPT-EN",
  191: "CLPTE-EN",
  192: "CLPT-EN es-es",
  193: "CLPT-EN es-la",
  194: "CLPT-EN fr-fr",
  195: "CLPT-EN fr-ca",
  196: "CLPT-EN pt-pt",
  197: "CLPT-EN pt-br",
  198: "CLPT-EN zh-cn",
  199: "CLPT-EN de",
  200: "CLPT-EN it",
  201: "CLPT-EN ja",
  202: "CLPT-EN ko",
  203: "CLPT-EN tr",
  204: "CLPTE-EN es-es",
  205: "CLPTE-EN es-la",
  206: "CLPTE-EN fr-fr",
  207: "CLPTE-EN fr-ca",
  208: "CLPTE-EN pt-pt",
  209: "CLPTE-EN pt-br",
  210: "CLPTE-EN zh-cn",
  211: "CLPTE-EN de",
  212: "CLPTE-EN it",
  213: "CLPTE-EN ja",
  214: "CLPTE-EN ko",
  215: "CLPTE-EN tr",
  216: "EPPnl",
  217: "EPPpl",
  218: "EPPro",
  219: "EPPcs",
  220: "EPPda",
  221: "EPPsv",
  222: "UCATda",
  223: "UCATro",
  224: "WPPpt-br",
  225: "WTMAfr-fr",
  226: "WTMAes-es",
  227: "WTMAuk",
  228: "EPPuk",
  229: "WSPfr-ca",
  230: "WSP L fr-ca",
  231: "WTMAde",
  232: "WTMApl",
  233: "EPPsk",
  234: "WTMAit",
  235: "WTMAcs",
  236: "WTMAsk",
  237: "GAMEfr-ca",
  238: "EPPar",
  240: "CTRSE",
  241: "CTRSE es-la",
  242: "CTRSE fr-ca",
  243: "CTRSE pt-br",
  // LAST LINE - DO NOT MOVE THIS COMMENT subTestIdToSubTestName
};

export const testIdToTestAbbreviation = {
  10: "CCAT",
  12: "CBST",
  13: "CPI",
  14: "MRAB",
  15: "CSAP",
  16: "WTMA",
  17: "TT",
  18: "TKT",
  19: "Excel",
  20: "Word",
  21: "Excel",
  22: "Word",
  23: "CAST",
  24: "SalesAP",
  29: "EPP",
  30: "CLIK",
  31: "WPP",
  32: "Excel",
  33: "Word",
  34: "EPP",
  35: "Excel",
  36: "Word",
  37: "UCAT",
  38: "PowerPoint",
  39: "Excel",
  40: "Word",
  41: "PowerPoint",
  42: "GAME",
  43: "CMRA",
  44: "Emotify",
  45: "Cognify",
  46: "WSP",
  47: "CCAT",
  48: "Illustrait",
  49: "CLIK",
  50: "WAA",
  51: "Emotify",
  52: "UCognify",
  53: "Excel 365",
  54: "Word 365",
  55: "PowerPoint 365",
  57: "Criteria Language Proficiency Test - English",
  "57_express": "Criteria Language Proficiency Test Express - English",
  59: "Criteria Think Reason Solve Express",
  // LAST LINE - DO NOT MOVE THIS COMMENT testIdToTestAbbreviation
};

export const getCheckedCandidatesIncompleteTestEventIds = (
  stageCandidates,
  checkedCandidatesList,
  fromResultsV2
) => {
  const checkedCandidatesIncompleteTestEventIds = [];

  checkedCandidatesList.forEach((checkedCandidate) => {
    const testEvents = getTestEvents(
      stageCandidates.get(checkedCandidate),
      fromResultsV2
    );

    if (!testEvents) {
      return;
    }

    const incompleteTestEventIds = [];

    testEvents.forEach((testEvent) => {
      if (testEvent.get("dateEventTaken") || testEvent.get("dateCompleted")) {
        return;
      }
      incompleteTestEventIds.push(testEvent.get("testEventId"));
    });

    incompleteTestEventIds.forEach((testEventId) => {
      // Do not push duplicates since Results V2 can have multiple testEvents with the same testEventId.
      if (!checkedCandidatesIncompleteTestEventIds.includes(testEventId)) {
        checkedCandidatesIncompleteTestEventIds.push(testEventId);
      }
    });
  });

  return checkedCandidatesIncompleteTestEventIds;
};

export const getCurrentJobHasActiveVideoInterview = (jobs, currentJobId) =>
  jobs &&
  jobs.size > 0 &&
  currentJobId &&
  jobs.get(String(currentJobId)) &&
  jobs.get(String(currentJobId)).get("videoInterviews") &&
  jobs.get(String(currentJobId)).get("videoInterviews").first().get("active");

export const getIsCurrentUserEvaluator = (jobData, userAccountId) => {
  const isCurrentUserEvaluator = {
    videoInterview: false,
    liveInterview: false,
  };

  if (!jobData) {
    return isCurrentUserEvaluator;
  }

  if (
    jobData.get("videoInterviews") &&
    jobData.get("videoInterviews").first().get("active") &&
    jobData
      .get("videoInterviews")
      .first()
      .get("evaluators")
      .find((evaluator) => evaluator.get("userAccountId") === userAccountId)
  ) {
    isCurrentUserEvaluator.videoInterview = true;
  }

  if (
    jobData.get("liveInterviews") &&
    jobData.get("liveInterviews").first() &&
    jobData
      .get("liveInterviews")
      .first()
      .get("evaluators")
      .find((evaluator) => evaluator.get("userAccountId") === userAccountId)
  ) {
    isCurrentUserEvaluator.liveInterview = true;
  }

  return isCurrentUserEvaluator;
};

export const getIsCurrentStageVideoInterviewStage = (
  jobs,
  currentJobId,
  currentStageId
) =>
  Boolean(
    jobs &&
      currentJobId &&
      jobs.get(String(currentJobId)) &&
      jobs.get(String(currentJobId)).get("videoInterviews") &&
      jobs
        .get(String(currentJobId))
        .get("videoInterviews")
        .first()
        .get("active") &&
      String(
        jobs
          .get(String(currentJobId))
          .get("videoInterviews")
          .first()
          .get("pipelineStageMapId")
      ) === String(currentStageId)
  );

export const getStageCandidates = (
  candidates,
  currentStageId,
  candidateStatus,
  allCandidates
) => {
  // All stage = big bucket of all candidates from all stages (including Not Hired stages)
  // Not Hired stage = big bucket of all candidates that are in stages with notHiredStatus flag = 1
  if (currentStageId === "all" || currentStageId === "archive") {
    return fromJS(allCandidates).filter(
      (candidate) => currentStageId === "all" || candidate.get("archive")
    );
  }
  if (candidates.get(String(currentStageId))) {
    return candidates
      .get(String(currentStageId))
      .filter(
        (candidate) =>
          candidateStatusMap[candidate.get("candidateStatus")] ===
          candidateStatus
      );
  }
  return candidates.get(String(currentStageId)) || new List();
};

// Returns true if the candidate's status is the same as the current candidateStatus
export const isCandidateStatusStageSubStatus = (
  candidates,
  candidateId,
  currentStageId,
  candidateStatus
) =>
  candidateStatusMap[
    candidates
      .get(String(currentStageId))
      .get(candidateId)
      .get("candidateStatus")
  ] === candidateStatus;

export const addDashesToEventID = (eventId) => {
  if (!eventId) {
    return "";
  }

  let dashedEventId = `${eventId.substring(0, 3)}-${eventId.substring(
    3,
    7
  )}-${eventId.substring(7, 11)}`;
  dashedEventId += eventId.length === 16 ? `-${eventId.substring(11)}` : "";
  return dashedEventId;
};

export const getCandidateName = (candidate) => {
  let candidateName = `${
    candidate.get("testTakerFirstName")
      ? candidate.get("testTakerFirstName").trim()
      : ""
  } ${
    candidate.get("testTakerLastName")
      ? candidate.get("testTakerLastName").trim()
      : ""
  }`;
  if (candidateName.length > 15) {
    candidateName = `${
      candidate.get("testTakerFirstName")
        ? candidate.get("testTakerFirstName").trim()
        : ""
    } ${
      candidate.get("testTakerLastName")
        ? candidate.get("testTakerLastName").trim().slice(0, 1)
        : ""
    }.`;
  }
  return candidateName;
};

export const orderedMapStringParser = (orderedMap) => {
  let mapString = "{";
  let index = 0;
  orderedMap.forEach((val, key) => {
    mapString += `"${key}":`;
    mapString += JSON.stringify(val);
    mapString += orderedMap.size - 1 === index ? "" : ",";
    index += 1;
  });
  mapString += "}";
  return mapString;
};

export const getCurrentJobHasActiveLiveInterview = (jobs, currentJobId) =>
  jobs &&
  jobs.size > 0 &&
  currentJobId &&
  jobs.get(String(currentJobId)) &&
  jobs.get(String(currentJobId)).get("liveInterviews") &&
  jobs.get(String(currentJobId)).get("liveInterviews").first();

export const checkIsLiveInterviewStage = (jobs, currentJobId, stageId) =>
  Boolean(
    jobs &&
      currentJobId &&
      jobs.get(String(currentJobId)) &&
      jobs.get(String(currentJobId)).get("liveInterviews") &&
      jobs
        .get(String(currentJobId))
        .get("liveInterviews")
        .find(
          (liveInterview) =>
            liveInterview.get("pipelineStageMapId") === Number(stageId)
        )
  );

export const getTestNameToDisplay = (test) => {
  // If Test is Cognify (testId == 45) then display testName (real value coming from subTestAbbreviation)
  let testNameToDisplay =
    Number(test.get("testId")) !== 45 &&
    subTestIdToSubTestName[test.get("subTestId")];

  if (!testNameToDisplay) {
    testNameToDisplay = test.get("testName");
  }

  if (testNameToDisplay && testNameToDisplay.length > 10) {
    testNameToDisplay = `${testNameToDisplay.slice(0, 6)}...`;
  }

  return testNameToDisplay;
};
